import { yupResolver } from '@hookform/resolvers/yup';
import { Grid } from '@mui/material';
import { useEffect, useState } from 'react';
import { FieldValues, FormProvider, useForm } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  AutomatedEmailCampaignType,
  getAutomatedEmailCampaign,
  saveAutomatedEmailCampaign,
} from 'wisestep-api';
import { ScrollableTabs, TabTextVariant } from 'wisestep-base';
import { InferType } from 'yup';

import { failed, success, warning } from '../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../utils/NotificationStore';
import { CampaignHeader, EmailCampaign, EmailTemplate } from '../../components';
import { defaultSchemaAndValues, getCampaignDefaultValues } from '../../schemas';
import {
  campaignTabs,
  createCampaignEntity,
  extractAllMessages,
  getCampaignUniqueId,
  routePath,
} from '../../utils';

interface CampaignLayoutProps {
  layoutType: AutomatedEmailCampaignType;
  header: React.ReactNode;
}

export const CampaignLayout = ({ layoutType, header }: CampaignLayoutProps) => {
  const [index, setIndex] = useState<number>(0);
  const selectedIndex = (index: number) => setIndex(index);
  const [searchParams] = useSearchParams();
  const campaignId = searchParams.get('id') ?? '';
  const navigate = useNavigate();
  const { schema, defaultValues } = defaultSchemaAndValues[layoutType];
  type FormValues = InferType<typeof schema>;

  const [formDefaultValues, setFormDefaultValues] = useState<FormValues>(defaultValues);

  const methods = useForm<FormValues>({
    resolver: yupResolver(schema),
    defaultValues,
    mode: 'onChange',
  });

  const { handleSubmit, reset } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await getAutomatedEmailCampaign(layoutType, campaignId);
        const defaultValues = getCampaignDefaultValues(response.data, layoutType);
        reset(defaultValues);
        setFormDefaultValues(defaultValues);
      } catch (error) {
        setNotification(failed('Failed to load campaign data'));
      }
    };

    fetchData();
  }, [campaignId, layoutType, reset, setNotification]);

  const checkDuplicate = async (data: FieldValues) => {
    try {
      const campaingUniqueId = getCampaignUniqueId(data, layoutType);
      const response = await getAutomatedEmailCampaign(layoutType, campaingUniqueId);
      return response.data;
    } catch {
      setNotification(failed('Something went wrong!'));
      return true;
    }
  };
  const onFormSubmit = async (data: FieldValues) => {
    if (!data.aiReplyEnabled && !data.manualReply && data.emailCheck) {
      setNotification(failed('Please select any one of the reply assistant'));
      return;
    }
    if (!data.id && layoutType !== AutomatedEmailCampaignType.DEFAULT) {
      const duplicate = await checkDuplicate(data);
      if (duplicate) {
        setNotification(warning('Campaign is already created'));
        return;
      }
    }

    try {
      const payload = createCampaignEntity(data, layoutType);
      const res = await saveAutomatedEmailCampaign(payload);
      setFormDefaultValues(res.data);
      navigate(routePath[layoutType]);
      setNotification(success('Email Campaign Saved Successfully'));
    } catch (e) {
      setNotification(failed('Something went wrong!'));
    }
  };

  const onError = (formErrors) => {
    const messages = extractAllMessages(formErrors);

    if (messages.length > 0) {
      setNotification(failed(messages.join('\n')));
    }
  };

  const handleReset = () => {
    setNotification(warning('Changes are discarded'));
    reset(formDefaultValues);
  };
  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onFormSubmit, onError)} onReset={handleReset}>
        <CampaignHeader name="emailCheck" skip={true}>
          {header}
        </CampaignHeader>
        <Grid xs={12}>
          <ScrollableTabs
            scrollList={campaignTabs}
            getTabIndex={selectedIndex}
            variant={TabTextVariant.SUB_TITLE}
          />
        </Grid>
        <Grid p={2}>
          {index == 0 && <EmailCampaign />}
          {index == 1 && <EmailTemplate title={'Default Email Template'} onReset={handleReset} />}
        </Grid>
      </form>
    </FormProvider>
  );
};
